import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { AuthLayout } from '@/shared/ui/auth-layout';
import { Button } from '@/shared/ui/button';
import { confirmationRedirect, pendingEmail } from '../../features/auth/pending-email';
import { supabase } from '../../shared/api/supabase';

/**
 * Shown after sign-up (and after a sign-in with an unconfirmed address): with email confirmation on there is no
 * session until the link in the email is followed. The text is the same whether or not the address was already
 * registered, because Supabase answers both the same way.
 */
export function CheckEmailPage() {
  const { t } = useTranslation('auth');
  useDocumentTitle(t('checkEmail.title'));
  const email = pendingEmail();
  const [state, setState] = useState<
    { kind: 'idle' | 'sending' | 'sent' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  return (
    <AuthLayout
      title={t('checkEmail.title')}
      footer={
        <p>
          {t('checkEmail.confirmed')} <Link to="/sign-in">{t('signUp.signIn')}</Link>
        </p>
      }
    >
      {email ? (
        <div className="space-y-4">
          <div className="space-y-1">
            <p>{t('checkEmail.sentTo')}</p>
            <p className="wrap-anywhere font-bold">{email}</p>
          </div>
          <p>{t('checkEmail.openIt')}</p>
          <p className="text-sm text-muted-foreground">{t('checkEmail.help')}</p>
          <Button
            variant="secondary"
            className="w-full"
            disabled={state.kind === 'sending'}
            onClick={async () => {
              setState({ kind: 'sending' });
              const { error } = await supabase.auth.resend({
                type: 'signup',
                email,
                options: { emailRedirectTo: confirmationRedirect() },
              });
              setState(error ? { kind: 'error', message: error.message } : { kind: 'sent' });
            }}
          >
            {t('checkEmail.resend')}
          </Button>
          {state.kind === 'sent' ? (
            <p role="status" className="text-sm">
              {t('checkEmail.resent')}
            </p>
          ) : null}
          {state.kind === 'error' ? (
            <p role="alert" className="text-sm text-destructive">
              {state.message}
            </p>
          ) : null}
          <p className="text-sm">
            <Link to="/sign-up">{t('checkEmail.changeEmail')}</Link>
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p>{t('checkEmail.noAddress')}</p>
          <p className="text-sm">
            <Link to="/sign-up">{t('signUp.submit')}</Link>
          </p>
        </div>
      )}
    </AuthLayout>
  );
}
